import type {
  AuthWithGoogleRequest,
  AuthorizeWithGoogleRequest,
  CheckEmailAvailabilityRequest,
  CheckPhoneAvailabilityRequest,
  ConfirmAuthorizationRequest,
  ConfirmResetPasswordRequest,
  LoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
  SendPhoneCodeRequest,
  VerifyEmailCodeRequest,
  VerifyEmailChangeRequest,
  VerifyPhoneCodeRequest,
} from "@vetty/contracts";
import { apiPaths } from "./paths";
import { requirePost, type SdkRequest } from "./request";

export const register = (request: SdkRequest, body: RegisterRequest) =>
  requirePost(request)(apiPaths.auth.register(), body);

export const login = (request: SdkRequest, body: LoginRequest) =>
  requirePost(request)(apiPaths.auth.login(), body);

export const logout = (request: SdkRequest) =>
  requirePost(request)(apiPaths.auth.logout());

export const refreshSession = (request: SdkRequest) =>
  requirePost(request)(apiPaths.auth.refresh());

export const validateSession = (request: SdkRequest) =>
  request.get(apiPaths.auth.validate());

export const authWithGoogle = (request: SdkRequest, body: AuthWithGoogleRequest) =>
  requirePost(request)(apiPaths.auth.google(), body);

export const authWithGoogleMobile = (
  request: SdkRequest,
  body: AuthWithGoogleRequest,
) => requirePost(request)(apiPaths.auth.googleMobile(), body);

export const confirmAuthorization = (
  request: SdkRequest,
  body: ConfirmAuthorizationRequest,
) => requirePost(request)(apiPaths.auth.authorizeConfirm(), body);

export const denyAuthorization = (
  request: SdkRequest,
  body: { authorizationId: string; reason?: string },
) => requirePost(request)(apiPaths.auth.authorizeDeny(), body);

export const authorizeWithGoogle = (
  request: SdkRequest,
  body: AuthorizeWithGoogleRequest,
) => requirePost(request)(apiPaths.auth.authorizeGoogle(), body);

export const initializeAuthorization = (
  request: SdkRequest,
  params: Record<string, string | number | undefined>,
) => request.get(apiPaths.auth.authorizeInitialize(), { params });

export const getAuthorizationStatus = (request: SdkRequest, id: string) =>
  request.get(apiPaths.auth.authorizeStatus(id));

export const exchangeCodeForToken = (request: SdkRequest, body: unknown) =>
  requirePost(request)(apiPaths.auth.token(), body);

export const refreshOAuthToken = (request: SdkRequest, body: unknown) =>
  requirePost(request)(apiPaths.auth.tokenRefresh(), body);

export const revokeOAuthToken = (request: SdkRequest, body: unknown) =>
  requirePost(request)(apiPaths.auth.tokenRevoke(), body);

export const introspectOAuthToken = (request: SdkRequest, body: unknown) =>
  requirePost(request)(apiPaths.auth.tokenIntrospect(), body);

export const requestPasswordReset = (
  request: SdkRequest,
  body: ResetPasswordRequest,
) => requirePost(request)(apiPaths.auth.passwordReset(), body);

export const confirmPasswordReset = (
  request: SdkRequest,
  body: ConfirmResetPasswordRequest,
) => requirePost(request)(apiPaths.auth.passwordResetConfirm(), body);

export const verifyPasswordResetToken = (request: SdkRequest, token: string) =>
  request.get(apiPaths.auth.passwordResetVerify(token));

export const sendPhoneCode = (request: SdkRequest, body: SendPhoneCodeRequest) =>
  requirePost(request)(apiPaths.auth.phoneSend(), body);

export const verifyPhoneCode = (request: SdkRequest, body: VerifyPhoneCodeRequest) =>
  requirePost(request)(apiPaths.auth.phoneVerify(), body);

export const checkPhoneAvailability = (
  request: SdkRequest,
  body: CheckPhoneAvailabilityRequest,
) => requirePost(request)(apiPaths.auth.phoneCheck(), body);

export const checkEmailAvailability = (
  request: SdkRequest,
  body: CheckEmailAvailabilityRequest,
) => requirePost(request)(apiPaths.auth.emailCheck(), body);

export const sendEmailVerification = (request: SdkRequest) =>
  requirePost(request)(apiPaths.auth.emailSend());

export const verifyEmailCode = (
  request: SdkRequest,
  body: VerifyEmailCodeRequest,
) => requirePost(request)(apiPaths.auth.emailVerify(), body);

export const verifyEmailChange = (
  request: SdkRequest,
  body: VerifyEmailChangeRequest,
) => requirePost(request)(apiPaths.auth.emailVerifyChange(), body);
